import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle, AlertTriangle } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/field';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/sonner';
import { AuthLayout, PasswordStrengthMeter } from '@/components/layout/auth-layout';
import { env } from '@/lib/env';

export const Route = createFileRoute('/team/accept/$token')({
  staticData: { public: true },
  component: AcceptPage,
});

const ROLE_LABEL: Record<string, string> = {
  owner: 'Właściciel',
  manager: 'Menedżer',
  receptionist: 'Recepcja',
  read_only: 'Tylko podgląd',
};

function AcceptPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const invitation = useQuery({
    queryKey: ['team', 'invitation', token],
    queryFn: async () => {
      const res = await fetch(`${env.apiBaseUrl}/api/team/invitations/${token}`);
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'invalid');
      }
      return (await res.json()) as { email: string; role: string; expires_at: number };
    },
    retry: false,
  });

  const [password, setPassword] = React.useState('');
  const [fullName, setFullName] = React.useState('');

  const accept = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${env.apiBaseUrl}/api/team/invitations/${token}/accept`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ password, full_name: fullName || undefined }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'accept_failed');
      }
      return res.json();
    },
    onSuccess: () => {
      toast.success('Zaproszenie zaakceptowane. Zaloguj się.');
      navigate({ to: '/login' as never });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Dołącz do zespołu</CardTitle>
          <CardDescription>Akceptacja zaproszenia do panelu pozyskajpacjenta.pl.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {invitation.isPending ? (
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : invitation.isError ? (
            <div className="rounded-md bg-error/10 p-3 text-sm text-error ring-1 ring-error/20">
              <AlertTriangle className="mr-1 inline size-4" aria-hidden="true" />
              Zaproszenie nieprawidłowe lub wygasłe. Poproś osobę zarządzającą kliniką o nowy link.
            </div>
          ) : invitation.data ? (
            <>
              <div className="rounded-md bg-surface p-3 text-sm ring-1 ring-black/5">
                <p>
                  Zaproszenie dla <strong>{invitation.data.email}</strong>
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Badge variant="soft">{ROLE_LABEL[invitation.data.role] ?? invitation.data.role}</Badge>
                  <span className="text-xs text-text-subtle">
                    wygasa {new Date(invitation.data.expires_at * 1000).toLocaleString('pl-PL')}
                  </span>
                </div>
              </div>
              <Field label="Imię i nazwisko" htmlFor="full_name">
                <Input
                  id="full_name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Anna Kowalska"
                />
              </Field>
              <Field
                label="Hasło"
                htmlFor="password"
                hint="Min. 8 znaków. Używaj długiego, unikalnego hasła."
                required
              >
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </Field>
              {password ? <PasswordStrengthMeter value={password} /> : null}
              <Button
                type="button"
                className="w-full"
                onClick={() => accept.mutate()}
                disabled={password.length < 8 || accept.isPending}
              >
                <CheckCircle className="size-4" aria-hidden="true" />
                {accept.isPending ? 'Akceptuję…' : 'Akceptuj zaproszenie'}
              </Button>
            </>
          ) : null}
        </CardContent>
      </Card>
    </AuthLayout>
  );
}
